import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMarketDataRequest } from '../../../store/reducers/orderReducer';
import {
    isMarketSocketConnected,
    onStatusChange,
} from '../../../services/socket/marketSocket';

/**
 * useMarketData
 * ─────────────────────────────────────────────────────────────────────────────
 * Same public API as before — returns { allMarketData }. Internally it now
 * works as follows:
 *
 *   - When the Socket.IO bridge is connected, marketSocketSaga is already
 *     dispatching merged market data into state.orders.allMarketData via
 *     tick batches. This hook just reads it; no setInterval needed.
 *
 *   - When the socket is DISCONNECTED, the hook falls back to the original
 *     behavior: dispatch `fetchMarketDataRequest` every 1s. This matches the
 *     pre-socket behavior exactly, so there is no regression.
 *
 *   - Status changes are observed live, so the interval is started/stopped
 *     within the same component lifetime without needing a remount.
 */
export const useMarketData = (orders) => {
    const dispatch = useDispatch();
    const allMarketData = useSelector((state) => state.orders.allMarketData);

    // Keep latest `orders` in a ref so the status-change listener always
    // dispatches with up-to-date data (without needing it in the deps array).
    const ordersRef = useRef(orders);
    useEffect(() => { ordersRef.current = orders; }, [orders]);

    useEffect(() => {
        if (!orders || orders.length === 0) return;

        // Always do one initial REST fetch so the page is populated even if
        // the socket hasn't connected yet (matches old behavior).
        dispatch(fetchMarketDataRequest(orders));

        let intervalId = null;
        const startPolling = () => {
            if (intervalId) return;
            intervalId = setInterval(() => {
                if (ordersRef.current && ordersRef.current.length > 0) {
                    dispatch(fetchMarketDataRequest(ordersRef.current));
                }
            }, 1000);
        };
        const stopPolling = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        // Only poll when the socket is NOT delivering ticks.
        if (!isMarketSocketConnected()) startPolling();

        // Toggle the poll on every connection state change.
        const offStatus = onStatusChange((connected) => {
            if (connected) {
                stopPolling();
            } else {
                // Force a one-shot fetch right after disconnect so the UI
                // isn't stuck on the last live tick during the gap.
                if (ordersRef.current && ordersRef.current.length > 0) {
                    dispatch(fetchMarketDataRequest(ordersRef.current));
                }
                startPolling();
            }
        });

        return () => {
            stopPolling();
            offStatus();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, orders && orders.length]);

    return { allMarketData };
};
